import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppStateInterface } from 'app/core/app-config/state/models/app-state.interface';
import { TokenInterface } from 'app/core/authentication/models';
import { UserDetailedInterface, UserInterface } from 'app/core/user/models';
import { LoadAdminUsers, LoadDetailedUsers, LoadUsers, SelectUser, UpdateUser } from 'app/core/user/state/actions';
import { UserStateInterface } from 'app/core/user/state/models';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { ErgoTeamMemberInterface } from '../models/ergo-team-member.model';
import { AddErgoTeamMember, CreateUser } from '../state/actions/user-create.action';
import { DeleteUser, RemoveErgoTeamMember } from '../state/actions/user-delete.action';
import { LoadSingleUser, LoadUserErgoTeam } from '../state/actions/user-load.action';

import { UserRestConnectorService } from './user-rest-connector.service';
import { UserServiceInterface } from './user-service.interface';

@Injectable()
export class UserService implements UserServiceInterface {

  private readonly _authenticatedUser$: BehaviorSubject<UserInterface>;

  private readonly _userNameCheck$: BehaviorSubject<{value: string, result: boolean}> =
    new BehaviorSubject<{value: string, result: boolean}>({ value: '', result: true });

  constructor(private readonly _store: Store<AppStateInterface>,
              private readonly _userRestConnector: UserRestConnectorService) {
    this._authenticatedUser$ = new BehaviorSubject<UserInterface>(undefined);
    this.onInit();
  }

  public onInit(): void {
    // subscribe to .authenticatedUser$ from the ngrx Store directly. This keeps Services 100% independent of one another
    // and relies on only ngrx Store for cross ngModule Service interactions. Compared to DI with the AuthService.
    this._store.select(({ auth }: AppStateInterface) => auth.token)
      .pipe(
        map((authToken: TokenInterface) => this._authenticatedUser$.next(authToken.loggedInUser)),
      );
  }

  public loadAll(): Observable<UserInterface[]> {
    // Dispatch the LoadUsers action to make sure they are loaded.
    this._store.dispatch(new LoadUsers());

    return this._store.select(({ user }: AppStateInterface) => user.users)
    .pipe(
      map((userEntities: { [id: number ]: UserInterface }) => Object.values(userEntities)),
    );
  }

  public loadAllDetailed(): Observable<UserDetailedInterface[]> {
    // Dispatch the LoadUsers action to make sure they are loaded.
    this._store.dispatch(new LoadDetailedUsers());

    return this.getAllDetailed();
  }

  public findAllUsersForAssignedToList(loadUsers: boolean = true): Observable<UserInterface[]> {
    // Dispatch the LoadUsers action to make sure they are loaded.
    if (loadUsers) {
      this._store.dispatch(new LoadUsers());
    }

    return this._store.select(({ user }: AppStateInterface) => user.users)
      .pipe(
        map((userEntities: { [id: number ]: UserInterface }) => Object.values(userEntities)),
      );
  }

  public findAllAdminUsers(): Observable<UserInterface[]> {
    this._store.dispatch(new LoadAdminUsers());

    return this._store.select(({ user }: AppStateInterface) => user.adminUsers);
  }

  public findOne(id: number): Observable<UserInterface> {

    return this._store.select(({ user }: AppStateInterface) => user)
      .pipe(
        map((userState: UserStateInterface) => {

          let user: UserInterface = undefined;
          if (userState.users[id]) {
            user = userState.users[id];
          }

          return user;
        }),
      );
  }

  public getSelectedUser(): Observable<UserInterface> {

    return this._store.select(({ user }: AppStateInterface) => user)
      .pipe(
        map((userState: UserStateInterface) => {

          let user: UserInterface = undefined;
          if (userState.users[userState.selectedUser]) {
            user = userState.users[userState.selectedUser];
          }

          return user;
        }),
      );
  }

  public getDetailedSelectedUser(): Observable<UserDetailedInterface> {

    return this._store.select(({ user }: AppStateInterface) => user)
      .pipe(
        map((userState: UserStateInterface) => {

          let detailedUser: UserDetailedInterface = undefined;
          if (userState.detailedUsers[userState.selectedUser]) {
            detailedUser = userState.detailedUsers[userState.selectedUser];
          }

          return detailedUser;
        }),
      );
  }

  public getAllDetailed(): Observable<UserDetailedInterface[]> {
    return this._store.select(({ user }: AppStateInterface) => user.detailedUsers)
    .pipe(
      map((userEntities: { [id: number ]: UserDetailedInterface }) => Object.values(userEntities)),
    );
  }

  public setSelectedUser(userId: number): Observable<number> {
    this._store.dispatch(new SelectUser(userId));

    return this._store.select(({ user }: AppStateInterface) => user.selectedUser);
  }

  public loadSingleUser(userId: number): Observable<UserInterface> {
    if(userId < 1) { return of(undefined); }
    this._store.dispatch(new LoadSingleUser(userId));

    return this.findOne(userId);
  }

  public getUserErgoTeam(user: UserInterface): Observable<UserInterface> {
    if(user.id < 1) { return of(undefined); }
    this._store.dispatch(new LoadUserErgoTeam(user));

    return this.findOne(user.id);
  }

  public addErgoTeamMember(user: UserInterface, memberToAdd: ErgoTeamMemberInterface): void {
    this._store.dispatch(new AddErgoTeamMember({ user, teamMember: memberToAdd }));
  }

  public deleteErgoTeamMember(user: UserInterface, memberToDelete: ErgoTeamMemberInterface): void {
    this._store.dispatch(new RemoveErgoTeamMember({ user, teamMember: memberToDelete }));
  }
  
  public updateUser(user: UserInterface): Observable<UserInterface> {
    this._store.dispatch(new UpdateUser(user));

    return this.findOne(user.id);
  }

  public updateUserOnly(user: UserInterface): void {
    this._store.dispatch(new UpdateUser(user));
  }

  public createUser(user: UserInterface): Observable<UserInterface> {
    this._store.dispatch(new CreateUser(user));

    return this._store.select(({ user }: AppStateInterface) => user.selectedProfileUser);
  }

  public checkUserName(userName: string): void {
    this._userRestConnector.checkUserName(userName)
    .pipe(take(1))
    .subscribe((checkResult: boolean) => this._userNameCheck$.next({ value: userName, result: checkResult }));
  }

  public isUserNameValid(): Observable<{ value: string, result: boolean }> {
    return this._userNameCheck$;
  }

  public deleteUser(userToDelete: UserInterface): void {
    this._store.dispatch(new DeleteUser(userToDelete));
  }

}
